export type PolicyMode = 'all_enabled' | 'allowlist' | 'disabled';
export interface PolicyRoute {
  connector_account_id: string;
  access_code: string;
}
export interface PolicyRouteOption extends PolicyRoute {
  display_name: string;
}
export interface DestinationPolicy {
  source: 'ubikeishon';
  place_id: string;
  display_name?: string;
  mode: PolicyMode | 'inherit' | 'extend';
  routes: PolicyRoute[];
  excluded_routes?: PolicyRoute[];
}
export type CountryPolicy = Omit<DestinationPolicy, 'source' | 'place_id' | 'display_name'> & { country_code: string };
export interface SearchPolicy {
  country_overrides?: CountryPolicy[];
  revision: number;
  default_mode: PolicyMode;
  default_routes: PolicyRoute[];
  destination_overrides: DestinationPolicy[];
}
export function routeKey(route: PolicyRoute) {
  return JSON.stringify([route.connector_account_id, route.access_code]);
}
export function toggleRoute(routes: PolicyRoute[], option: PolicyRoute, selected: boolean) {
  const rest = routes.filter((route) => routeKey(route) !== routeKey(option));
  return selected
    ? [...rest, { connector_account_id: option.connector_account_id, access_code: option.access_code }]
    : rest;
}
const identities = (rows: PolicyRoute[]) =>
  rows.map(({ connector_account_id, access_code }) => ({ connector_account_id, access_code }));
export function policyPayload(policy: SearchPolicy, reason: string) {
  return {
    expected_revision: policy.revision,
    reason: reason.trim(),
    default_mode: policy.default_mode,
    default_routes: policy.default_mode === 'allowlist' ? identities(policy.default_routes) : [],
    ...(policy.country_overrides ? { country_overrides: policy.country_overrides.map(cleanRule) } : {}),
    destination_overrides: policy.destination_overrides.map((row) => ({
      ...row,
      ...(row.excluded_routes ? { excluded_routes: row.mode === 'extend' ? identities(row.excluded_routes) : [] } : {}),
      routes: row.mode === 'allowlist' || row.mode === 'extend' ? identities(row.routes) : [],
    })),
  };
}
export function policyValid(policy: SearchPolicy, reason: string) {
  return Boolean(
    reason.trim() &&
      reason.trim().length <= 1000 &&
      policy.destination_overrides.length <= 200 &&
      (policy.country_overrides?.length ?? 0) <= 250 &&
      (policy.country_overrides ?? []).every((row) => /^[A-Z]{2}$/.test(row.country_code)) &&
      new Set((policy.country_overrides ?? []).map((row) => row.country_code)).size ===
        (policy.country_overrides?.length ?? 0) &&
      [...policy.destination_overrides, ...(policy.country_overrides ?? [])].every((row) => {
        const add = new Set(row.routes.map(routeKey));
        return row.mode !== 'extend' || !(row.excluded_routes ?? []).some((r) => add.has(routeKey(r)));
      }) &&
      policy.destination_overrides.every((row) => /^plc_.+/.test(row.place_id)) &&
      new Set(policy.destination_overrides.map((row) => row.place_id)).size === policy.destination_overrides.length,
  );
}

function cleanRule<
  T extends { mode: DestinationPolicy['mode']; routes: PolicyRoute[]; excluded_routes?: PolicyRoute[] },
>(row: T): T {
  return {
    ...row,
    routes: row.mode === 'allowlist' || row.mode === 'extend' ? identities(row.routes) : [],
    ...(row.excluded_routes ? { excluded_routes: row.mode === 'extend' ? identities(row.excluded_routes) : [] } : {}),
  };
}
// Display only; the authenticated backend is authoritative for geographical previews.
export function effectivePolicyRoutes(
  policy: SearchPolicy,
  options: PolicyRouteOption[],
  destination?: DestinationPolicy,
  country?: string,
) {
  let result: PolicyRouteOption[] = [];
  for (const rule of [
    { mode: policy.default_mode, routes: policy.default_routes } as CountryPolicy,
    policy.country_overrides?.find((c) => c.country_code === country),
    destination,
  ]) {
    if (!rule || rule.mode === 'inherit') continue;
    if (rule.mode === 'disabled') {
      result = [];
      continue;
    }
    if (rule.mode === 'all_enabled') {
      result = options;
      continue;
    }
    const selected = new Set([...rule.routes, ...(rule.mode === 'extend' ? result : [])].map(routeKey));
    const excluded = new Set((rule.excluded_routes ?? []).map(routeKey));
    result = options.filter((r) => selected.has(routeKey(r)) && !excluded.has(routeKey(r)));
  }
  return result;
}

// Explicit recovery only: preserve every rule and mode, including empty allowlists.
export function recoverInactiveRoutes(policy: SearchPolicy, available: PolicyRoute[]) {
  const active = new Set(available.map(routeKey));
  const affected: { scope: 'default' | 'country' | 'destination'; name: string; removed: PolicyRoute[] }[] = [];
  const clean = <T extends { routes: PolicyRoute[]; excluded_routes?: PolicyRoute[] }>(
    row: T,
    scope: 'default' | 'country' | 'destination',
    name: string,
  ): T => {
    const removed = [...row.routes, ...(row.excluded_routes ?? [])].filter((r) => !active.has(routeKey(r)));
    if (removed.length)
      affected.push({ scope, name, removed: [...new Map(removed.map((r) => [routeKey(r), r])).values()] });
    return {
      ...row,
      routes: row.routes.filter((r) => active.has(routeKey(r))),
      ...(row.excluded_routes ? { excluded_routes: row.excluded_routes.filter((r) => active.has(routeKey(r))) } : {}),
    };
  };
  const defaultRule = clean({ routes: policy.default_routes }, 'default', '');
  return {
    affected,
    policy: {
      ...policy,
      default_routes: defaultRule.routes,
      ...(policy.country_overrides
        ? { country_overrides: policy.country_overrides.map((r) => clean(r, 'country', r.country_code)) }
        : {}),
      destination_overrides: policy.destination_overrides.map((r) =>
        clean(r, 'destination', r.display_name ?? r.place_id),
      ),
    },
  };
}
